import { useCallback, useEffect, useState } from 'react';

/**
 * Persisted collapse state for the right-hand filter rail.
 *
 * Mirrors the left nav's SidebarCollapseContext one for one: a boolean in
 * localStorage under a `<thing>_collapsed` key, default open, written on every
 * toggle so it restores on reload. Kept as a tiny hook rather than joining the
 * nav's context because the two rails are independent choices: collapsing the
 * nav must not fold the filters and vice versa.
 *
 * Every mounted rail shares the value through a storage-style event so two
 * rails on one page (the profile tabs) never disagree.
 *
 * A page can also HOLD the rail open (requestRailOpen / releaseRailOpen), the
 * rail's twin of the nav's requestPageCollapse: counted, never persisted, and
 * a manual fold while it is held wins for the rest of the hold (Asana
 * 1219902279124885 Decision 1, the offer builder's focus mode).
 */
export const FILTER_RAIL_STORAGE_KEY = 'filter_rail_collapsed';
const CHANGE_EVENT = 'filter-rail-collapse-change';

const read = (): boolean => {
  try {
    return localStorage.getItem(FILTER_RAIL_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Module state rather than React state so every mounted rail and the page
 * holding the request read one value, exactly like the nav's session veto.
 * Counted because effects double-invoke in StrictMode and a route change can
 * mount the next page before the previous one unmounts.
 */
let openRequestCount = 0;
/**
 * Set by a manual toggle while a request is held: the user folded (or
 * re-opened) the rail for this hold. Reset when the last request releases so
 * the stored preference is back in force. Never written to storage.
 */
let heldCollapsed = false;

const notify = () => window.dispatchEvent(new Event(CHANGE_EVENT));

/** The value the rail renders from: the hold's override while held, else the preference. */
const readEffective = (): boolean => (openRequestCount > 0 ? heldCollapsed : read());

/** Ask for the rail to be open for as long as the caller is mounted. Pair with releaseRailOpen. */
export const requestRailOpen = () => {
  openRequestCount += 1;
  notify();
};

export const releaseRailOpen = () => {
  openRequestCount = Math.max(0, openRequestCount - 1);
  if (openRequestCount === 0) heldCollapsed = false;
  notify();
};

/** Test-only reset for the module state. */
export const __resetRailOpenRequestsForTests = () => {
  openRequestCount = 0;
  heldCollapsed = false;
};

export const useFilterRailCollapse = () => {
  const [collapsed, setCollapsed] = useState<boolean>(readEffective);

  useEffect(() => {
    const sync = () => setCollapsed(readEffective());
    // A request may have been placed between the initial read and this effect.
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setAndPersist = useCallback((next: boolean) => {
    try {
      localStorage.setItem(FILTER_RAIL_STORAGE_KEY, String(next));
    } catch {
      /* storage unavailable: session-only */
    }
    // An explicit unfold (the My Cards magnifier) must show while held too.
    if (openRequestCount > 0) heldCollapsed = next;
    setCollapsed(readEffective());
    notify();
  }, []);

  const toggle = useCallback(() => {
    if (openRequestCount > 0) {
      // Respected for the rest of the hold; the preference is not touched, so
      // the next surface the user visits is back on what they chose globally.
      heldCollapsed = !heldCollapsed;
      setCollapsed(readEffective());
      notify();
      return;
    }
    setAndPersist(!read());
  }, [setAndPersist]);

  return { collapsed, toggle, setCollapsed: setAndPersist, requestRailOpen, releaseRailOpen };
};

export default useFilterRailCollapse;
